import { collection, query, where, orderBy, limit, startAfter, getDocs, doc, updateDoc, getDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../../firebase';
import { NotificationRepository } from '../../../domain/notifications/repositories/NotificationRepository';
import { Notification } from '../../../domain/notifications/entities/Notification';
import { logger } from '../../../shared/utils/logger';

export class FirestoreNotificationRepository extends NotificationRepository {
  constructor() {
    super();
    this.collection = collection(db, 'notifications');
  }

  async getNotifications(userId, { limit: limitCount = 20, lastVisible = null } = {}) {
    try {
      logger.debug('🔄 Chargement des notifications depuis Firestore...');
      let q = query(
        this.collection,
        where('recipientId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      if (lastVisible) {
        q = query(q, startAfter(lastVisible));
      }

      const snapshot = await getDocs(q);
      const notifications = [];
      let lastVisibleDoc = null;

      for (const docSnapshot of snapshot.docs) {
        const data = docSnapshot.data();
        
        // Si la notification a un senderId, récupérer les infos de l'utilisateur
        let sender = null;
        if (data.senderId) {
          const userRef = doc(db, 'users', data.senderId);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            sender = {
              uid: userDoc.id,
              name: userDoc.data().displayName,
              photoURL: userDoc.data().photoURL
            };
          }
        }

        notifications.push(
          new Notification({
            id: docSnapshot.id,
            type: data.type,
            recipientId: data.recipientId,
            senderId: data.senderId,
            sender,
            postId: data.postId,
            activity: data.activity,
            read: data.read,
            createdAt: data.createdAt?.toDate()
          })
        );
        lastVisibleDoc = docSnapshot;
      }

      return {
        notifications,
        lastVisible: lastVisibleDoc,
        hasMore: notifications.length === limitCount
      };
    } catch (error) {
      throw new Error(`Failed to fetch notifications: ${error.message}`);
    }
  }

  async markAsRead(notificationId) {
    try {
      const notificationRef = doc(this.collection, notificationId);
      await updateDoc(notificationRef, { read: true });
    } catch (error) {
      throw new Error(`Failed to mark notification as read: ${error.message}`);
    }
  }

  async markAllAsRead(userId) {
    try {
      const batch = writeBatch(db);
      const q = query(
        this.collection,
        where('recipientId', '==', userId),
        where('read', '==', false)
      );
      
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(docSnapshot => {
        const docRef = doc(this.collection, docSnapshot.id);
        batch.update(docRef, { read: true });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error in markAllAsRead:', error);
      throw new Error(`Failed to mark all notifications as read: ${error.message}`);
    }
  }

  async getUnreadCount(userId) {
    try {
      const q = query(
        this.collection,
        where('recipientId', '==', userId),
        where('read', '==', false)
      );
      
      const snapshot = await getDocs(q);
      const count = snapshot.size;
      return count;
    } catch (error) {
      throw new Error(`Failed to get unread count: ${error.message}`);
    }
  }
} 